import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1610641818989-c2051b5e2cfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjByZXNvcnQlMjBwb29sfGVufDB8fHx8MTc4MzY0MzIyMXww&ixlib=rb-4.1.0&q=85";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Application received — pending approval");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ir-bg)]" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/"><Logo size="md" /></Link>
          <h1 className="font-display text-4xl mt-10 text-[var(--ir-text)]">Request Access</h1>
          <p className="text-[var(--ir-muted)] mt-2">Apply to join. Once approved, we'll issue your private 10-digit PIN number.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-[var(--ir-text)]">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} placeholder="Jane Traveler" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-[var(--ir-text)]">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-[var(--ir-text)]">Phone <span className="text-[var(--ir-muted)]">(optional)</span></Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="register-phone-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[var(--ir-text)]">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="At least 6 characters" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12" data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit Application <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <p className="text-[var(--ir-muted)] mt-8 text-sm">
            Already a member?{" "}
            <Link to="/login" className="text-[var(--ir-blue)] font-medium hover:underline" data-testid="register-to-login-link">Sign in</Link>
          </p>
        </motion.div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ir-blue-dark)]/70 via-[var(--ir-blue-dark)]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Join the few who travel<br /><span className="text-gradient-orange italic">for far less.</span></h2>
        </div>
      </div>
    </div>
  );
};

export default Register;
